<template>
  <span class="claude-runner-card-face">
    <WTooltip
      position="right"
      :max-width="440"
      :disabled="!view.lines.length"
      :section-tag="`${sectionTag}-tooltip`"
    >
      <WPills
        type="static"
        :color="PILL_COLORS[view.state]"
        :icon="icon"
        :label="view.label"
        label-class="claude-runner-card-face__label"
        size="small"
        max-width="100%"
        :section-tag="`${sectionTag}-pill`"
      />
      <template #content>
        <div class="claude-runner-card-face__hover">
          <strong class="claude-runner-card-face__title">{{ view.hover.title }}</strong>
          <p v-for="(note, index) in view.hover.notes ?? []" :key="`note-${index}`">{{ note }}</p>
          <dl v-if="view.hover.facts?.length" class="claude-runner-card-face__facts">
            <template v-for="fact in view.hover.facts" :key="fact.label">
              <dt>{{ fact.label }}</dt>
              <dd>{{ fact.value }}</dd>
            </template>
          </dl>
          <div v-if="view.hover.tail" class="claude-runner-card-face__tail">
            <span class="claude-runner-card-face__muted">{{ view.hover.tail.title }}</span>
            <span
              v-for="(line, index) in view.hover.tail.lines"
              :key="`tail-${index}`"
              class="claude-runner-card-face__tail-line"
            >{{ line }}</span>
          </div>
          <span v-if="view.hover.hint" class="claude-runner-card-face__hint claude-runner-card-face__muted">
            {{ view.hover.hint }}
          </span>
        </div>
      </template>
    </WTooltip>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiAlertCircleOutline, mdiUpdate } from '@mdi/js'

import type { RunnerCardState, RunnerCardView } from '@/services/claude-fleet'

import WPills from '@wasabi/actions/WPills.vue'
import WTooltip from '@wasabi/data-visualization/WTooltip.vue'

/**
 * The runner card's face and its hover; ClaudeRunnerCard decides what a click does. The colour and the button beside
 * it already say the state, so only a warning (!) or an update in progress adds an icon, which a sidebar collapsed to
 * icons still shows.
 */
const props = defineProps<{ view: RunnerCardView; sectionTag: string }>()

const icon = computed(() => {
  if (props.view.warning) return mdiAlertCircleOutline
  return props.view.updating ? mdiUpdate : undefined
})

const PILL_COLORS: Record<RunnerCardState, 'green' | 'yellow' | 'gray' | 'blue'> = {
  running: 'green',
  attention: 'yellow',
  starting: 'gray',
  off: 'gray'
}

</script>

<style scoped>
/* Shrinks with the sidebar, so a long label truncates instead of pushing the buttons out. */
.claude-runner-card-face,
.claude-runner-card-face > :deep(span) {
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
}

.claude-runner-card-face :deep(.v-chip) {
  flex-shrink: 1;
  min-width: 0;
}

/* The tooltip is dark, so muted text dims the tooltip's own colour rather than taking the page's grey. */
.claude-runner-card-face__hover {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 440px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.4;
}

.claude-runner-card-face__hover p {
  margin: 0;
}

.claude-runner-card-face__title {
  font-size: 14px;
  font-weight: 700;
}

.claude-runner-card-face__muted,
.claude-runner-card-face__facts dt {
  opacity: 0.72;
}

.claude-runner-card-face__facts {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 4px 12px;
  margin: 0;
}

.claude-runner-card-face__facts dt {
  white-space: nowrap;
}

.claude-runner-card-face__facts dd {
  margin: 0;
  overflow-wrap: anywhere;
}

/* One line each: the drawer's Live section shows them whole. */
.claude-runner-card-face__tail {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.claude-runner-card-face__tail-line {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.claude-runner-card-face__tail,
.claude-runner-card-face__hint {
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
}
</style>
